import type { ReponseAEnvoyer } from '@/lib/api/feed'

/**
 * Le magasin de la file hors-ligne — [DECISIONS §111].
 *
 * **Quatre opérations, aucune requête.** L'ordre de renvoi (RG-9.5) est calculé
 * par la file elle-même et non par un index de base : c'est la règle métier qui
 * décide, et une règle métier tenue par un index de stockage n'est vérifiable
 * qu'avec ce stockage sous la main.
 *
 * **Deux implémentations.** IndexedDB, qui survit à la fermeture de l'onglet ;
 * et la mémoire, qui ne survit à rien mais fonctionne partout — navigation
 * privée de certains Safari, stockage refusé par une politique, environnement
 * de test. Avant ce repli, l'absence d'IndexedDB voulait dire pas de file du
 * tout : la réponse était perdue exactement comme avant §111.
 */

export interface ReponseEnFile extends ReponseAEnvoyer {
  /** La LISTE d'où venait la réponse : l'objectif du jour en dépend (§101). */
  contexte: string
}

export interface Magasin {
  /** Toutes les entrées, sans ordre garanti — la file les trie elle-même. */
  lire(): Promise<ReponseEnFile[]>
  /** `client_uuid` en clé : réécrire la même réponse la remplace. */
  ecrire(reponse: ReponseEnFile): Promise<void>
  effacer(uuid: string): Promise<void>
  compter(): Promise<number>
}

const BASE = 'upfac-hors-ligne'
const TABLE = 'reponses'

export function magasinEnMemoire(): Magasin {
  const entrees = new Map<string, ReponseEnFile>()

  return {
    lire: () => Promise.resolve([...entrees.values()]),
    ecrire: (r) => { entrees.set(r.client_uuid, r); return Promise.resolve() },
    effacer: (uuid) => { entrees.delete(uuid); return Promise.resolve() },
    compter: () => Promise.resolve(entrees.size),
  }
}

/**
 * IndexedDB, ou la mémoire s'il n'est pas là.
 *
 * **`localStorage` ne convenait pas** : synchrone — il bloque le rendu au moment
 * précis où le candidat répond —, plafonné à 5 Mo tous usages confondus, et
 * limité aux chaînes, donc une sérialisation par écriture.
 */
export function magasinIndexedDB(): Magasin {
  let ouverture: Promise<IDBDatabase | null> | null = null
  const repli = magasinEnMemoire()

  const base = (): Promise<IDBDatabase | null> => {
    ouverture ??= new Promise((resoudre) => {
      if (typeof indexedDB === 'undefined') {
        resoudre(null)

        return
      }

      const demande = indexedDB.open(BASE, 1)

      demande.onupgradeneeded = () => {
        const db = demande.result

        if (!db.objectStoreNames.contains(TABLE)) {
          db.createObjectStore(TABLE, { keyPath: 'client_uuid' })
        }
      }

      demande.onsuccess = () => { resoudre(demande.result) }
      // Refusé par la politique du navigateur : on ne lève pas. Perdre la
      // persistance est un dommage ; ne plus pouvoir répondre en serait un
      // autre, et bien plus grand.
      demande.onerror = () => { resoudre(null) }
    })

    return ouverture
  }

  async function agir<T>(
    mode: IDBTransactionMode,
    travail: (table: IDBObjectStore) => IDBRequest,
    defaut: T,
  ): Promise<T> {
    const db = await base()

    if (db === null) return defaut

    return new Promise<T>((resoudre) => {
      const demande = travail(db.transaction(TABLE, mode).objectStore(TABLE))

      demande.onsuccess = () => { resoudre(demande.result as T) }
      demande.onerror = () => { resoudre(defaut) }
    })
  }

  return {
    lire: async () =>
      (await base()) === null ? repli.lire() : agir<ReponseEnFile[]>('readonly', (t) => t.getAll(), []),
    ecrire: async (r) => {
      if ((await base()) === null) return repli.ecrire(r)

      await agir('readwrite', (t) => t.put(r), undefined)
    },
    effacer: async (uuid) => {
      if ((await base()) === null) return repli.effacer(uuid)

      await agir('readwrite', (t) => t.delete(uuid), undefined)
    },
    compter: async () =>
      (await base()) === null ? repli.compter() : agir<number>('readonly', (t) => t.count(), 0),
  }
}
